import { Link } from "react-router-dom";
import { Instagram, ArrowUpRight, MapPin, Clock, Star } from "lucide-react";
import { LogoFull } from "./Logo";
import { scrollToSection } from "./SmoothScroll";
import { SITE, waLink } from "../data/site";

const goTo = (hash) => (e) => {
  e.preventDefault();
  window.history.replaceState(null, "", hash);
  scrollToSection(hash);
};

const QUICK_LINKS = [
  { label: "Home", to: "/#home" },
  { label: "Products", to: "/#products" },
  { label: "Gallery", to: "/#gallery" },
  { label: "Contact", to: "/#contact" },
];

const WhatsAppIcon = ({ size = 15, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.52 3.48A11.87 11.87 0 0 0 12.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.27-1.64a11.9 11.9 0 0 0 5.76 1.47h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.19-3.45-8.42ZM12.04 21.8a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.97 1-3.63-.24-.37a9.86 9.86 0 0 1-1.51-5.25c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.98 2.9a9.8 9.8 0 0 1 2.89 6.98c0 5.45-4.44 9.88-9.89 9.88Zm5.42-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2-1.42.25-.7.25-1.29.18-1.42-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

const ACTIONS = [
  {
    label: "Get Directions",
    href: SITE.mapsLink,
    icon: MapPin,
    testid: "footer-directions-button",
  },
  {
    label: "Chat on WhatsApp",
    href: waLink("Hi Ramaani Electricals, I have a question."),
    icon: WhatsAppIcon,
    testid: "footer-whatsapp-link",
  },
  {
    label: "Follow on Instagram",
    href: SITE.instagram,
    icon: Instagram,
    testid: "footer-instagram-link",
  },
  {
    label: "Leave a Google Review",
    href: SITE.reviewUrl,
    icon: Star,
    testid: "footer-google-rating-link",
  },
];

export const Footer = () => (
  <footer className="border-t border-white/10 bg-silt/60" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <LogoFull className="h-16" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
            One Stop Shop · Since {SITE.since}
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-fog">{SITE.tagline}</p>
          <div className="mt-6 space-y-2 text-sm text-fog">
            <p className="flex items-start gap-2">
              <MapPin size={15} className="mt-0.5 shrink-0 text-blue" /> {SITE.address}
            </p>
            <p className="flex items-center gap-2">
              <Clock size={15} className="shrink-0 text-blue" /> {SITE.hours}
            </p>
            <p className="flex items-center gap-2 pl-6 text-xs text-ash">{SITE.hoursSunday}</p>
          </div>
        </div>

        <div className="lg:col-span-6">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ash">Explore</p>
          <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  onClick={goTo(l.to.replace("/", ""))}
                  className="text-sm text-fog transition-colors duration-300 hover:text-blue"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {ACTIONS.map((a) => (
          <a
            key={a.label}
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={a.testid}
            className="group flex items-center justify-center gap-2.5 rounded-full border border-white/15 px-5 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:border-blue/50 hover:text-blue hover:shadow-[0_0_28px_rgba(46,107,255,0.15)]"
          >
            <a.icon size={15} className="text-blue" />
            {a.label}
            <ArrowUpRight
              size={13}
              className="text-ash transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-blue"
            />
          </a>
        ))}
      </div>

      <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
        <p className="font-mono text-xs text-ash">
          © 2026 {SITE.name}. All rights reserved.
        </p>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-ash">
          Sheshaiah Street · Vijayawada
        </p>
      </div>
    </div>
  </footer>
);
